/**
 * @file inter-transfer-request.tsx
 * @module app/modules/warehouse/inter-transfer-request/inter-transfer-request
 * @description Implements the inter transfer request workflow for warehouse management.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Button, Card, Col, Nav, Row, Tab } from "react-bootstrap";
import Pagination from "../../../../Global/pagination";
import { useNavigate } from "react-router-dom";
import {
  interTransferRequest,
} from "../../../../api/apiEndPoints";
import {
  Add,
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  PAGE_LIMIT,
  Piece,
  TransferRequestAccepted,
  TransferRequestSubmitted,
  PermissionModule
} from "../../../../utils/constants";
import APICallService from "../../../../api/apiCallService";
import Loader from "../../../../Global/loader";
import { useAuth } from "../../auth";
import Method from "../../../../utils/methods";
import ViewRequestedGoodsProducts from "../../../modals/view-requested-goods-products";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useNavigationHistory } from "../../../../utils/useNavigationHistory";

const NAIROBI_TIMEZONE = "Africa/Nairobi";

const formatDateTimeInNairobi = (date: string) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: NAIROBI_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(parsed);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}`;
};

const InterTransferRequest = () => {
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const [fetchLoader, setFetchLoader] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState<any>(1);
  const [pageLimit, setPageLimit] = useState<any>(PAGE_LIMIT);
  const { currentUser }: any = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const [requestList, setRequestList] = useState<any>([]);
  const [showProductModal, setShowProductModal] = useState<any>(false);
  const [currentData, setCurrentData] = useState<any>();
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchTransferRequests(
        page,
        pageLimit,
        activeTab,
        selectedWarehouse?.value || ""
      );
      setFetchLoader(false);
    })();
  }, [selectedWarehouse?.value]);
  const fetchTransferRequests = async (
    page: number,
    limit: number,
    tab: number,
    warehouse?: any
  ) => {
    let params: any = {
      pageNo: page,
      limit: limit,
      sortOrder: -1,
      sortKey: "_createdAt",
      needCount: true,
      warehouseTransferRequest: true,
    };
    if (warehouse) {
      params.warehouseId = warehouse;
    }
    if (tab == 1) {
      // params.status = [1];
      params = {
        ...params,
        "status[0]": 1,
      };
    } else if (tab == 2) {
      params = {
        ...params,
        "status[0]": 2,
        "status[1]": 6,
        "status[2]": 5,
        "status[3]": 8,
      };
    } else if (tab == 3) {
      params = {
        ...params,
        "status[0]": 7,
      };
    } else if (tab == 4) {
      params = {
        ...params,
        "status[0]": 3,
      };
    } else if (tab == 5) {
      params = {
        ...params,
        "status[0]": 4,
      };
    }
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse.value;
    }

    setLoading(true);
    const apiService = new APICallService(
      interTransferRequest.listTransferRequest,
      params,
      "",
      "",
      "",
      "",
      PermissionModule.WarehouseInterTransfer,
      selectedWarehouse?.value || ""
    );
    const response = await apiService.callAPI();
    if (response) {
      setRequestList(response.records);
      setTotalRecords(response.total);
    }
    setLoading(false);
  };
  const handleCurrentPage = async (val: number) => {
    if (val === page || val.toString() === "...") return;
    setLoading(true);
    setPage(val);
    // setKey(interTransferList.page, val);
    await fetchTransferRequests(
      val,
      pageLimit,
      activeTab,
      selectedWarehouse?.value || ""
    );
    setLoading(false);
  };
  const handleNextPage = async (val: number) => {
    setLoading(true);
    setPage(val + 1);
    // setKey(interTransferList.page, val + 1);
    await fetchTransferRequests(
      val + 1,
      pageLimit,
      activeTab,
      selectedWarehouse?.value || ""
    );
    setLoading(false);
  };
  const handlePreviousPage = async (val: number) => {
    setLoading(true);
    setPage(val - 1);
    // setKey(interTransferList.page, val - 1);
    await fetchTransferRequests(
      val - 1,
      pageLimit,
      activeTab,
      selectedWarehouse?.value || ""
    );
    setLoading(false);
  };
  const handlePageLimit = async (event: any) => {
    setLoading(true);
    setPage(1);
    setTotalRecords(0);
    // setKey(interTransferList.page, 1);
    // setKey(interTransferList.limit, parseInt(event.target.value));
    // setTests([]);
    await setPageLimit(parseInt(event.target.value));
    await fetchTransferRequests(
      1,
      event.target.value,
      activeTab,
      selectedWarehouse?.value || ""
    );
    setLoading(false);
  };
  const getQuantityTypes = (val: any) => {
    if (val == CartonWithDozens) {
      return "C";
    } else if (val == Dozen) {
      return "D";
    } else if (val == Piece) {
      return "P";
    } else if (val == CartonWithPieces) {
      return "C";
    } else {
      return "";
    }
  };
  const handleProduct = (data: any) => {
    setShowProductModal(true);
    setCurrentData(data);
  };
  return (
    <>
      {currentData && showProductModal ? (
        <ViewRequestedGoodsProducts
          show={showProductModal}
          onHide={() => {
            setShowProductModal(false);
            setCurrentData(undefined);
          }}
          products={currentData?.variants || []}
        />
      ) : (
        <></>
      )}
      <div className="p-5">
        <Row className="align-items-center">
          {/* {currentUser?.userType == Admin ? ( */}
            <Col xs={12} className="mb-5">
              <div className="d-flex flex-wrap justify-content-between align-items-center">
                <h1 className="fs-22 fw-bolder">Inter transfer request</h1>
                {
                  Method.hasPermission(PermissionModule.WarehouseInterTransfer,Add,currentUser) ? <>{!fetchLoader ? (
                  <Button className="uw-btn uw-btn-primary"
                    onClick={() =>
                      navigate("/warehouse/create-transfer-request")
                    }
                  >
                    Create new request
                  </Button>
                ) : (
                  <></>
                )}</> : <></>
                }
              </div>
            </Col>
           {/* ) : (
            <></>
          )}  */}
          {!fetchLoader ? (
            <>
              <div className="custom-tabContainer">
                <Tab.Container activeKey={activeTab}>
                  <Row>
                    <Col lg={"auto"} className="mb-6">
                      <div className="bg-light border border-r8px p-3 px-md-6">
                        <Nav variant="pills">
                          <Nav.Item>
                            <Nav.Link
                              eventKey={1}
                              onClick={() => {
                                setActiveTab(1);
                                setPage(1);
                                fetchTransferRequests(
                                  page,
                                  pageLimit,
                                  1,
                                  selectedWarehouse?.value || ""
                                );
                              }}
                            >
                              Pending Requests
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link
                              eventKey={2}
                              onClick={() => {
                                setActiveTab(2);
                                setPage(1);
                                fetchTransferRequests(
                                  page,
                                  pageLimit,
                                  2,
                                  selectedWarehouse?.value || ""
                                );
                              }}
                            >
                              Accepted Requests
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link
                              eventKey={3}
                              onClick={() => {
                                setActiveTab(3);
                                setPage(1);
                                fetchTransferRequests(
                                  page,
                                  pageLimit,
                                  3,
                                  selectedWarehouse?.value || ""
                                );
                              }}
                            >
                              Completed
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link
                              eventKey={4}
                              onClick={() => {
                                setActiveTab(4);
                                setPage(1);
                                fetchTransferRequests(
                                  page,
                                  pageLimit,
                                  4,
                                  selectedWarehouse?.value || ""
                                );
                              }}
                            >
                              Cancelled
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link
                              eventKey={5}
                              onClick={() => {
                                setActiveTab(5);
                                setPage(1);
                                fetchTransferRequests(
                                  page,
                                  pageLimit,
                                  5,
                                  selectedWarehouse?.value || ""
                                );
                              }}
                            >
                              Rejected
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>
                      </div>
                    </Col>
                    <Tab.Content>
                      <Tab.Pane eventKey={1}>
                        <Col xs={12}>
                          <Card className=" border mb-7">
                            <Card.Body className="px-7">
                              <div className="table-responsive">
                                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0">
                                  <thead>
                                    <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                                      <th className="min-w-250px">
                                        Request initiated by
                                      </th>
                                      <th className="min-w-200px">
                                        Request initiated on
                                      </th>
                                      <th className="min-w-200px">Quantity</th>
                                      <th className="min-w-150px">Status</th>
                                      <th className="min-w-200px"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!loading ? (
                                      <>
                                        {requestList.length > 0 ? (
                                          requestList.map((val: any) => (
                                            <tr key={val.id}>
                                              <td>
                                                <span className="fs-15 fw-600">
                                                  {val?.requestor?.name
                                                    ? val?.requestor?.name
                                                    : val?.receiverWarehouse
                                                        ?.name}
                                                </span>
                                              </td>
                                              <td className="fw-600 fs-15">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val._createdAt)
                                                  : "-"}
                                              </td>
                                              <td>
                                                <div className="d-flex flex-column align-items-start">
                                                  <span className="fw-600 fs-15">
                                                    {val?.variants.map(
                                                      (
                                                        item: any,
                                                        index: number
                                                      ) => {
                                                        return `${
                                                          item?.stockCount
                                                        } ${getQuantityTypes(
                                                          item?.quantityType
                                                        )}${
                                                          index <
                                                          val?.variants.length -
                                                            1
                                                            ? ", "
                                                            : " "
                                                        }`;
                                                      }
                                                    )}
                                                  </span>
                                                  {/* <Button
                                                    variant="link"
                                                    onClick={() => {
                                                      handleProduct(val);
                                                    }}
                                                  >
                                                    <span className="text-decoration-underline fs-15 fw-600">
                                                      View Products
                                                    </span>
                                                  </Button> */}
                                                </div>
                                              </td>
                                              <td>
                                                <div
                                                  style={{
                                                    height: "33px",
                                                    width: "100px",
                                                    borderRadius: "4px",
                                                    backgroundColor:
                                                      val.status == 6
                                                        ? "#e5f7de"
                                                        : "#fff4d9",
                                                  }}
                                                  className="d-flex align-items-center justify-content-center fs-14 fw-600"
                                                >
                                                  {val?.status ==
                                                    TransferRequestSubmitted ||
                                                  val?.status ===
                                                    TransferRequestAccepted
                                                    ? "In Progress"
                                                    : "Out for Delivery"}
                                                </div>
                                              </td>
                                              <td>
                                                <Button className="uw-btn uw-btn-primary"
                                                  onClick={() =>
                                                    navigateTo(
                                                      "/warehouse/all-inter-transfer-request/inter-transfer-details",
                                                      {
                                                        state: {
                                                          _id: val._id,
                                                        },
                                                      }
                                                    )
                                                  }
                                                >
                                                  View Details
                                                </Button>
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td
                                              colSpan={5}
                                              className="text-center"
                                            >
                                              <span className="fs-15 fw-600">
                                                No Data Found.
                                              </span>
                                            </td>
                                          </tr>
                                        )}
                                      </>
                                    ) : (
                                      <tr>
                                        <td colSpan={5}>
                                          <div className="w-100 d-flex justify-content-center">
                                            <Loader loading={loading} />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Tab.Pane>
                      <Tab.Pane eventKey={2}>
                        <Col xs={12}>
                          <Card className=" border mb-7">
                            <Card.Body className="px-7">
                              <div className="table-responsive">
                                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0">
                                  <thead>
                                    <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                                      <th className="min-w-250px">
                                        Request initiated by
                                      </th>
                                      <th className="min-w-200px">
                                        Request initiated on
                                      </th>
                                      <th className="min-w-200px">
                                        Request accepted on
                                      </th>
                                      <th className="min-w-200px">Quantity</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!loading ? (
                                      <>
                                        {requestList.length > 0 ? (
                                          requestList.map((val: any) => (
                                            <tr key={val.id}>
                                              <td>
                                                <span className="fs-15 fw-600">
                                                  {val?.requestor?.name
                                                    ? val?.requestor?.name
                                                    : val?.receiverWarehouse
                                                        ?.name}
                                                </span>
                                              </td>
                                              <td className="fs-15 fw-600">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val._createdAt)
                                                  : "-"}
                                              </td>
                                              <td className="fs-15 fw-600">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val.statusUpdatedAt)
                                                  : "-"}
                                              </td>
                                              <td>
                                                <div className="d-flex flex-column align-items-start">
                                                  <span className="fs-15 fw-600">
                                                    {val?.variants.map(
                                                      (
                                                        item: any,
                                                        index: number
                                                      ) => {
                                                        return `${
                                                          item?.stockCount
                                                        } ${getQuantityTypes(
                                                          item?.quantityType
                                                        )}${
                                                          index <
                                                          val?.variants.length -
                                                            1
                                                            ? ", "
                                                            : " "
                                                        }`;
                                                      }
                                                    )}
                                                  </span>
                                                  {/* <Button
                                                    variant="link"
                                                    onClick={() => {
                                                      handleProduct(val);
                                                    }}
                                                  >
                                                    <span className="text-decoration-underline fs-15 fw-600">
                                                      View Products
                                                    </span>
                                                  </Button> */}
                                                </div>
                                              </td>
                                              <td>
                                                <Button className="uw-btn uw-btn-primary"
                                                  onClick={() =>
                                                    navigate(
                                                      "/warehouse/all-inter-transfer-request/inter-transfer-details",
                                                      {
                                                        state: {
                                                          _id: val._id,
                                                        },
                                                      }
                                                    )
                                                  }
                                                >
                                                  View Details
                                                </Button>
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td
                                              colSpan={5}
                                              className="text-center"
                                            >
                                              <span className="fs-15 fw-600">
                                                No Data Found
                                              </span>
                                            </td>
                                          </tr>
                                        )}
                                      </>
                                    ) : (
                                      <tr>
                                        <td colSpan={5}>
                                          <div className="w-100 d-flex justify-content-center">
                                            <Loader loading={loading} />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Tab.Pane>
                      <Tab.Pane eventKey={3}>
                        <Col xs={12}>
                          <Card className=" border mb-7">
                            <Card.Body className="px-7">
                              <div className="table-responsive">
                                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0">
                                  <thead>
                                    <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                                      <th className="min-w-250px">
                                        Request initiated by
                                      </th>
                                      <th className="min-w-200px">
                                        Request initiated on
                                      </th>
                                      <th className="min-w-200px">
                                        Request completed on
                                      </th>
                                      <th className="min-w-200px">Quantity</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!loading ? (
                                      <>
                                        {requestList.length > 0 ? (
                                          requestList.map((val: any) => (
                                            <tr key={val.id}>
                                              <td>
                                                <span className="fs-15 fw-600">
                                                  {val?.requestor?.name
                                                    ? val?.requestor?.name
                                                    : val?.receiverWarehouse
                                                        ?.name}
                                                </span>
                                              </td>
                                              <td className="fs-15 fw-600">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val._createdAt)
                                                  : "-"}
                                              </td>
                                              <td className="fs-15 fw-600">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val.statusUpdatedAt)
                                                  : "-"}
                                              </td>
                                              <td>
                                                <div className="d-flex flex-column align-items-start">
                                                  <span className="fs-15 fw-600">
                                                    {val?.variants.map(
                                                      (
                                                        item: any,
                                                        index: number
                                                      ) => {
                                                        return `${
                                                          item?.stockCount
                                                        } ${getQuantityTypes(
                                                          item?.quantityType
                                                        )}${
                                                          index <
                                                          val?.variants.length -
                                                            1
                                                            ? ", "
                                                            : " "
                                                        }`;
                                                      }
                                                    )}
                                                  </span>
                                                  {/* <Button
                                                    variant="link"
                                                    onClick={() => {
                                                      handleProduct(val);
                                                    }}
                                                  >
                                                    <span className="text-decoration-underline fs-15 fw-600">
                                                      View Products
                                                    </span>
                                                  </Button> */}
                                                </div>
                                              </td>
                                              <td>
                                                <Button className="uw-btn uw-btn-primary"
                                                  onClick={() =>
                                                    navigate(
                                                      "/warehouse/all-inter-transfer-request/inter-transfer-details",
                                                      {
                                                        state: {
                                                          _id: val._id,
                                                        },
                                                      }
                                                    )
                                                  }
                                                >
                                                  View Details
                                                </Button>
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td
                                              colSpan={5}
                                              className="text-center"
                                            >
                                              <span className="fs-15 fw-600">
                                                No Data Found
                                              </span>
                                            </td>
                                          </tr>
                                        )}
                                      </>
                                    ) : (
                                      <tr>
                                        <td colSpan={5}>
                                          <div className="w-100 d-flex justify-content-center">
                                            <Loader loading={loading} />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Tab.Pane>
                      <Tab.Pane eventKey={4}>
                        <Col xs={12}>
                          <Card className=" border mb-7">
                            <Card.Body className="px-7">
                              <div className="table-responsive">
                                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0">
                                  <thead>
                                    <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                                      <th className="min-w-250px">
                                        Request initiated by
                                      </th>
                                      <th className="min-w-200px">
                                        Request initiated on
                                      </th>
                                      <th className="min-w-200px">
                                        Request cancelled on
                                      </th>
                                      <th className="min-w-200px">Quantity</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!loading ? (
                                      <>
                                        {requestList.length > 0 ? (
                                          requestList.map((val: any) => (
                                            <tr key={val.id}>
                                              <td>
                                                <span className="fs-15 fw-600">
                                                  {val?.requestor?.name
                                                    ? val?.requestor?.name
                                                    : val?.receiverWarehouse
                                                        ?.name}
                                                </span>
                                              </td>
                                              <td className="fs-15 fw-600">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val._createdAt)
                                                  : "-"}
                                              </td>
                                              <td className="fs-15 fw-600">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val.statusUpdatedAt)
                                                  : "-"}
                                              </td>
                                              <td>
                                                <div className="d-flex flex-column align-items-start">
                                                  <span className="fs-15 fw-600">
                                                    {val?.variants.map(
                                                      (
                                                        item: any,
                                                        index: number
                                                      ) => {
                                                        return `${
                                                          item?.stockCount
                                                        } ${getQuantityTypes(
                                                          item?.quantityType
                                                        )}${
                                                          index <
                                                          val?.variants.length -
                                                            1
                                                            ? ", "
                                                            : " "
                                                        }`;
                                                      }
                                                    )}
                                                  </span>
                                                  {/* <Button
                                                    variant="link"
                                                    onClick={() => {
                                                      handleProduct(val);
                                                    }}
                                                  >
                                                    <span className="text-decoration-underline fs-15 fw-600">
                                                      View Products
                                                    </span>
                                                  </Button> */}
                                                </div>
                                              </td>
                                              <td>
                                                <Button className="uw-btn uw-btn-primary"
                                                  onClick={() =>
                                                    navigate(
                                                      "/warehouse/all-inter-transfer-request/inter-transfer-details",
                                                      {
                                                        state: {
                                                          _id: val._id,
                                                        },
                                                      }
                                                    )
                                                  }
                                                >
                                                  View Details
                                                </Button>
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td
                                              colSpan={5}
                                              className="text-center"
                                            >
                                              <span className="fs-15 fw-600">
                                                No Data Found
                                              </span>
                                            </td>
                                          </tr>
                                        )}
                                      </>
                                    ) : (
                                      <tr>
                                        <td colSpan={5}>
                                          <div className="w-100 d-flex justify-content-center">
                                            <Loader loading={loading} />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Tab.Pane>
                      <Tab.Pane eventKey={5}>
                        <Col xs={12}>
                          <Card className=" border mb-7">
                            <Card.Body className="px-7">
                              <div className="table-responsive">
                                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0">
                                  <thead>
                                    <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                                      <th className="min-w-250px">
                                        Request initiated by
                                      </th>
                                      <th className="min-w-200px">
                                        Request initiated on
                                      </th>
                                      <th className="min-w-200px">
                                        Request rejected on
                                      </th>
                                      <th className="min-w-200px">Quantity</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!loading ? (
                                      <>
                                        {requestList.length > 0 ? (
                                          requestList.map((val: any) => (
                                            <tr key={val.id}>
                                              <td>
                                                <span className="fs-15 fw-600">
                                                  {val?.requestor?.name
                                                    ? val?.requestor?.name
                                                    : val?.receiverWarehouse
                                                        ?.name}
                                                </span>
                                              </td>
                                              <td className="fs-15 fw-600">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val._createdAt)
                                                  : "-"}
                                              </td>
                                              <td className="fs-15 fw-600">
                                                {val?._createdAt
                                                  ? formatDateTimeInNairobi(val.statusUpdatedAt)
                                                  : "-"}
                                              </td>
                                              <td>
                                                <div className="d-flex flex-column align-items-start">
                                                  <span className="fs-15 fw-600">
                                                    {val?.variants.map(
                                                      (
                                                        item: any,
                                                        index: number
                                                      ) => {
                                                        return `${
                                                          item?.stockCount
                                                        } ${getQuantityTypes(
                                                          item?.quantityType
                                                        )}${
                                                          index <
                                                          val?.variants.length -
                                                            1
                                                            ? ", "
                                                            : " "
                                                        }`;
                                                      }
                                                    )}
                                                  </span>
                                                  {/* <Button
                                                    variant="link"
                                                    onClick={() => {
                                                      handleProduct(val);
                                                    }}
                                                  >
                                                    <span className="text-decoration-underline fs-15 fw-600">
                                                      View Products
                                                    </span>
                                                  </Button> */}
                                                </div>
                                              </td>
                                              <td>
                                                <Button className="uw-btn uw-btn-primary"
                                                  onClick={() =>
                                                    navigate(
                                                      "/warehouse/all-inter-transfer-request/inter-transfer-details",
                                                      {
                                                        state: {
                                                          _id: val._id,
                                                        },
                                                      }
                                                    )
                                                  }
                                                >
                                                  View Details
                                                </Button>
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td
                                              colSpan={5}
                                              className="text-center"
                                            >
                                              <span className="fs-15 fw-600">
                                                No Data Found
                                              </span>
                                            </td>
                                          </tr>
                                        )}
                                      </>
                                    ) : (
                                      <tr>
                                        <td colSpan={5}>
                                          <div className="w-100 d-flex justify-content-center">
                                            <Loader loading={loading} />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Tab.Pane>
                    </Tab.Content>
                  </Row>
                </Tab.Container>
              </div>
              {totalRecords > 0 && !loading ? (
                <Pagination
                  totalRecords={totalRecords}
                  currentPage={page}
                  handleCurrentPage={handleCurrentPage}
                  handleNextPage={handleNextPage}
                  handlePreviousPage={handlePreviousPage}
                  handlePageLimit={handlePageLimit}
                  pageLimit={pageLimit}
                />
              ) : (
                <></>
              )}
            </>
          ) : (
            <div className="d-flex justify-content-center">
              <Loader loading={fetchLoader} />
            </div>
          )}
        </Row>
      </div>
    </>
  );
};
export default InterTransferRequest;
